import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { setSentryUser, clearSentryUser } from '@raporgor/utils';
import type { AuthState, FirmStatus } from '../types';
import { isAdminUser } from '../types';
import { authApi } from '../api/auth';
import { dataApi } from '../api/data';
import { hasPermission } from '../config/rolePermissions';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  dismissAnnouncement: (id: number) => void;
  hasReportAccess: (reportKey: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const FIRM_STATUS_KEY = 'firm_status';
const ANNOUNCEMENTS_KEY = 'announcements';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    firmStatus: null,
    announcements: [],
  });

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (storedToken && storedUser) {
        try {
          // Verify token is still valid and get fresh data
          const meResponse = await authApi.getMe(storedToken);

          // Parse firm status
          const firmStatus: FirmStatus | null = meResponse.firmStatus ? {
            status: meResponse.firmStatus as FirmStatus['status'],
            deactivationReason: meResponse.deactivationReason,
            licenseExpiresAt: meResponse.licenseExpiresAt,
          } : null;

          setState({
            user: meResponse.user,
            token: storedToken,
            isAuthenticated: true,
            isLoading: false,
            firmStatus,
            announcements: meResponse.announcements || [],
          });

          // Set Sentry user context — sadece stable id + tenant + role (PII kuralı).
          setSentryUser(Sentry, {
            id: meResponse.user.id,
            firmId: meResponse.user.firmId,
            role: meResponse.user.role,
          });

          // Update localStorage with fresh data
          localStorage.setItem(USER_KEY, JSON.stringify(meResponse.user));
          if (firmStatus) {
            localStorage.setItem(FIRM_STATUS_KEY, JSON.stringify(firmStatus));
          }
        } catch {
          // Token expired or invalid, clear storage
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          localStorage.removeItem(FIRM_STATUS_KEY);
          localStorage.removeItem(ANNOUNCEMENTS_KEY);
          clearSentryUser(Sentry);
          setState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            firmStatus: null,
            announcements: [],
          });
        }
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login(email, password);

    // Parse firm status
    const firmStatus: FirmStatus | null = response.firmStatus ? {
      status: response.firmStatus as FirmStatus['status'],
      deactivationReason: response.deactivationReason,
      licenseExpiresAt: response.licenseExpiresAt,
    } : null;

    // Store in localStorage
    localStorage.setItem(TOKEN_KEY, response.token);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    if (firmStatus) {
      localStorage.setItem(FIRM_STATUS_KEY, JSON.stringify(firmStatus));
    }

    setState({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      isLoading: false,
      firmStatus,
      announcements: response.announcements || [],
    });

    // Set Sentry user context — sadece stable id + tenant + role (PII kuralı).
    setSentryUser(Sentry, {
      id: response.user.id,
      firmId: response.user.firmId,
      role: response.user.role,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(FIRM_STATUS_KEY);
    localStorage.removeItem(ANNOUNCEMENTS_KEY);

    // Clear Sentry user context
    clearSentryUser(Sentry);

    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      firmStatus: null,
      announcements: [],
    });
  }, []);

  const dismissAnnouncement = useCallback(async (id: number) => {
    try {
      // Call API to dismiss
      await dataApi.dismissAnnouncement(id);
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
    }

    // Remove from state regardless of API success
    setState(prev => ({
      ...prev,
      announcements: prev.announcements.filter(a => a.id !== id),
    }));
  }, []);

  const hasReportAccess = useCallback((reportKey: string): boolean => {
    // FirmAdmin role always has full access
    if (isAdminUser(state.user)) return true;

    // Check permissions using the hardcoded config based on user's firm role name
    return hasPermission(state.user?.firmRoleName, reportKey);
  }, [state.user]);

  const isAdmin = isAdminUser(state.user);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      isAdmin,
      dismissAnnouncement,
      hasReportAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper to get token for API calls
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
